import React, { ReactElement, useState, useCallback, useRef, useEffect } from 'react';

import useScroll from 'hooks/useScroll';
import { usePopup } from 'hooks';

import { Label } from 'components';
import ImageItem from 'components/atoms/ImageItem';
import PopupImageGuide, { Props as PopupImageGuideProps, TGuideType, TUploadType } from 'components/molecules/Popup/ImageGuide';

import * as S from './style';
import { ReactComponent as UploadIcon } from 'assets/img/common/ic_object_camera_fill.svg';

import { ALERT_IMG_UPLOAD_COUNT_OVER, LABEL_IMG_UPLOAD } from 'commons/ZimssaString';
import { ImageData, getFulfilledImageList, hasTotallyScrolled, getUploadImageList } from 'commons/ZimssaHelper';
import { GUIDE_TYPE, UPLOAD_TYPE, SERVICE_TYPE } from 'commons/ZimssaConstant';

export interface ImageObject {
  [key: string]: ImageState;
}

export interface ImageState {
  tempInputName: string;
  tempValue: string;
  imageState: 'fulfilled' | 'rejected' | 'pending';
}

export interface Props {
  serviceType: 'FAMILY_MOVE' | 'SMALL_MOVE' | 'MOVE_CLEANING';
  type?: TGuideType;
  uploadType?: TUploadType;
  inputName: string;
  imgDir: string;
  guideMessageList?: string[];
  onChangeHandler?: (values: string[]) => void;
}

class ImageStateDto {
  tempInputName: string;
  tempValue: string;
  imageState: 'fulfilled' | 'rejected' | 'pending';
  id: number;

  static count: number = 0;

  constructor(inputName: string = '', value: string = '', state: 'fulfilled' | 'rejected' | 'pending' = 'pending') {
    this.tempInputName = inputName;
    this.tempValue = value;
    this.imageState = state;
    this.id = ImageStateDto.count++;
  }
}

const MAX_HEIGHT_BY_ITEM = 5;
const MAX_IMAGE_COUNT = 21;

function FileImgListUploader({
  serviceType,
  type,
  uploadType,
  inputName,
  imgDir,
  guideMessageList,
  onChangeHandler,
}: Props): ReactElement {
  const imageListContainerRef = useRef<HTMLDivElement>(null);
  const [uploadImageInfoObject, setUploadImageInfoObject] = useState<ImageObject>({});
  const { scrollHeight, scrollTop, clientHeight } = useScroll({ ref: imageListContainerRef });
  const { isShow, popupAction } = usePopup();

  const popupImageGuideProps: PopupImageGuideProps = {
    type: type ?? GUIDE_TYPE.FIELD_PHOTO,
    uploadType: uploadType ?? UPLOAD_TYPE.MULTIPLE,
    inputName: inputName ? inputName : '',
    imgDir: imgDir ? imgDir : '',
    isShow: isShow,
    onClickPopupAction: popupAction,
    onChangeMultiHandler: (inputRef: React.RefObject<HTMLInputElement>, imgDir: string) => {
      onChangeFile && onChangeFile(inputRef, imgDir);
      popupAction();
    },
  };

  const hasNeverScrolled = scrollTop === 0;

  const onChangeFile = useCallback((inputRef: React.RefObject<HTMLInputElement>, imgDir: string) => {
    try {
      const fulfilledDataList = getFulfilledImageList(uploadImageInfoObject);

      let imageList = getUploadImageList(inputRef, imgDir);

      if (fulfilledDataList.length + imageList.length > MAX_IMAGE_COUNT) {
        alert(ALERT_IMG_UPLOAD_COUNT_OVER);

        const addableImageCount = MAX_IMAGE_COUNT - fulfilledDataList.length;

        if (addableImageCount <= 0) {
          return;
        }

        imageList = imageList.slice(0, addableImageCount);
      }

      const promisesById: { [id: string]: Promise<ImageData> } = {};
      const newImageInfoObject: ImageObject = imageList.reduce((prevImageObject, currImage) => {
        const pendingImageData = currImage.data;

        const newImageInfo = new ImageStateDto(pendingImageData.file.name, pendingImageData.data, 'pending');
        prevImageObject[newImageInfo.id] = newImageInfo;

        promisesById[newImageInfo.id] = currImage.promise;

        return prevImageObject;
      }, {} as ImageObject);

      setUploadImageInfoObject((prev: ImageObject) => {
        return { ...prev, ...newImageInfoObject };
      });

      Object.entries(promisesById).forEach(([id, promise]) => {
        const newImageInfo: ImageState = {
          tempInputName: '',
          tempValue: '',
          imageState: 'pending',
        };

        promise.then((imageData: ImageData) => {
          newImageInfo.tempInputName = imageData.file.name;
          newImageInfo.tempValue = imageData.data;
          newImageInfo.imageState = 'fulfilled';
        }).catch((failedImageData: ImageData) => {
          newImageInfo.tempInputName = failedImageData.file.name;
          newImageInfo.tempValue = failedImageData.data;
          newImageInfo.imageState = 'rejected'

          if (failedImageData.error) {
            alert(failedImageData.error);
          }
        }).finally(() => setUploadImageInfoObject((prev: ImageObject) => {
          const newState = { ...prev };

          newState[id] = newImageInfo;
          return newState;
        }));
      });
    } catch (error) {
      let message = 'Unknown Error';

      if (error instanceof Error) {
        if (error.message === 'NO FILE SELECTED') return;
        message = error.message;
      }

      alert(message);
    }
  }, [imgDir, inputName, onChangeHandler, uploadImageInfoObject]);

  useEffect(() => {
    const fulfilledDataList = getFulfilledImageList(uploadImageInfoObject);

    onChangeHandler && onChangeHandler(fulfilledDataList);
  }, [uploadImageInfoObject])

  const isMoveCleaning = serviceType === SERVICE_TYPE.MOVE_CLEANING;

  return (
      <S.FileImgListUploaderContainer>
        <S.FileImgContainer>
          <PopupImageGuide {...popupImageGuideProps} />
          <S.InputFileWrapper style={{width: isMoveCleaning ? '100%' : '120px'}} onClick={() => popupAction()}>
            <S.LabelWrapper>
              <UploadIcon />
              <Label
                fontStyle={'body2'}
                color={'gray_opacity_600'}
                cursor={'pointer'}
                contents={LABEL_IMG_UPLOAD}
              />
            </S.LabelWrapper>
          </S.InputFileWrapper>
        </S.FileImgContainer>
        <S.GuideTextContainer>
          {
            guideMessageList?.map((message, index) => (
              <S.GuideText key={index}>
                {message}
              </S.GuideText>
            ))
          }
        </S.GuideTextContainer>
        <S.Gradient
          visible={
            Object.keys(uploadImageInfoObject).length >= MAX_HEIGHT_BY_ITEM
            && !hasNeverScrolled
          }
          direction={'DOWN_SIDE'}
        />
        <S.FileImgListContainer maxHeightByItem={MAX_HEIGHT_BY_ITEM} ref={imageListContainerRef}>
          {Object.entries(uploadImageInfoObject).map(([id, imageInfo]) => {
            return <ImageItem
              inputName={imageInfo.tempInputName}
              value={imageInfo.tempValue}
              imageState={imageInfo.imageState}
              onDeleteHandler={() => {
                setUploadImageInfoObject((prev) => {
                  const copy = {...prev};

                  delete copy[id];
                  return copy;
                });
              }}
              key={id}
            />
          })}
        </S.FileImgListContainer>
        <S.Gradient
          visible={
            Object.keys(uploadImageInfoObject).length >= MAX_HEIGHT_BY_ITEM
            && !hasTotallyScrolled(scrollHeight, scrollTop, clientHeight)
          }
          direction={'UP_SIDE'}
        />
      </S.FileImgListUploaderContainer>
  );
}

export default FileImgListUploader;
